import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Divider, Chip, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, Slider, useMediaQuery, useTheme, Tabs, Tab } from '@mui/material';
import { getAis, createAi, deleteAi, deleteCollection, updateAi, updateCollectionVersion, updateCollectionSettings } from '../services/api';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';

// Updated interface to match our new data model
interface Collection {
  _id: string;
  aiId: string;
  collectionName: string;
  version: string;
  isPublic: boolean;
  instruction?: string;
  temperature?: number;
  qaPairsLimit?: number;
  debugOutline?: boolean;
  color?: string;
  chatbotTitle?: string;
}

interface AI {
  id: string;
  title: string;
  domain: string;
  description: string;
  notificationEmail?: string;
  collections: Collection[];
}

const MyAis = () => {
  const [ais, setAis] = useState<AI[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [editingAiId, setEditingAiId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editVersionName, setEditVersionName] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingInstructions, setEditingInstructions] = useState('');
  const [editingTemperature, setEditingTemperature] = useState(0.9);
  const [editingQaPairsLimit, setEditingQaPairsLimit] = useState(5);
  const [editingNotificationEmail, setEditingNotificationEmail] = useState('');
  const [currentEditingCollection, setCurrentEditingCollection] = useState<{aiId: string, collectionId: string} | null>(null);
  const [settingsTabValue, setSettingsTabValue] = useState(0);

  useEffect(() => {
    loadAis();
  }, []);

  const loadAis = () => {
    getAis()
      .then((res) => {
        console.log('AI data:', res.data);
        setAis(res.data);
      })
      .catch((err) => console.error('Error loading AIs:', err));
  };

  const handleCreate = () => {
    console.log('Creating AI:', newTitle);
    if (!newTitle.trim()) return;
    createAi({ title: newTitle, domain: 'Education' })
      .then((res) => {
        setAis([...ais, res.data]);
        setNewTitle('');
      })
      .catch((err) => console.error('Error creating AI:', err));
  };

  const handleStartEdit = (ai: AI) => {
    setEditingAiId(ai.id);
    setEditTitle(ai.title);
  };
  
  const handleCancelEdit = () => {
    setEditingAiId(null);
    setEditTitle('');
  };
  
  const handleSaveEdit = (aiId: string) => {
    if (!editTitle.trim()) return;
    updateAi(aiId, { title: editTitle })
      .then((res) => {
        // Update the local state with the updated AI
        setAis(ais.map(ai => ai.id === aiId ? { ...ai, title: editTitle } : ai));
        setEditingAiId(null);
        setEditTitle('');
      })
      .catch((err) => console.error('Error updating AI:', err));
  };

  const handleDeleteAi = (id: string) => {
    if (window.confirm('Are you sure you want to delete this AI and all its collections?')) {
      deleteAi(id)
        .then(() => {
          setAis(ais.filter((ai) => ai.id !== id));
        })
        .catch((err) => console.error('Error deleting AI:', err));
    }
  };

  const handleDeleteCollection = (aiId: string, collectionId: string) => {
    if (window.confirm('Are you sure you want to delete this collection? This action cannot be undone.')) {
      deleteCollection(aiId, collectionId)
        .then(() => {
          // Update the local state to remove the deleted collection from UI
          const updatedAis = ais.map(ai => {
            if (ai.id === aiId) {
              return {
                ...ai,
                collections: ai.collections.filter(collection => collection._id !== collectionId)
              };
            }
            return ai;
          });
          setAis(updatedAis);
        })
        .catch((err) => console.error('Error deleting collection:', err));
    }
  };
  
  const handleCloneVersion = (aiId: string, sourceCollection: Collection) => {
    // Get existing version names to avoid duplicates
    const existingVersions = ais
      .find(ai => ai.id === aiId)?.collections
      .map(collection => collection.version) || [];
    
    // Generate a unique version name based on the source version
    const baseVersionName = `${sourceCollection.version} copy`;
    let newVersionName = baseVersionName;
    let counter = 2;
    
    while (existingVersions.includes(newVersionName)) {
      newVersionName = `${baseVersionName} (${counter})`;
      counter++;
    }
    
    // Navigate to create version page with the source collection ID and pre-filled version name
    navigate(`/create-version/${aiId}?fromCollectionId=${sourceCollection._id}&versionName=${encodeURIComponent(newVersionName)}`);
  };

  const handleStartEditVersion = (collection: Collection) => {
    setEditingVersionId(collection._id);
    setEditVersionName(collection.version);
  };
  
  const handleCancelEditVersion = () => {
    setEditingVersionId(null);
    setEditVersionName('');
  };
  
  const handleSaveVersionName = (aiId: string, collectionId: string) => {
    if (!editVersionName.trim()) return;
    updateCollectionVersion(aiId, collectionId, editVersionName)
      .then(() => {
        // Update the local state with the updated version name
        setAis(ais.map(ai => {
          if (ai.id === aiId) {
            return {
              ...ai,
              collections: ai.collections.map(collection => 
                collection._id === collectionId 
                  ? { ...collection, version: editVersionName } 
                  : collection
              )
            };
          }
          return ai;
        }));
        setEditingVersionId(null);
        setEditVersionName('');
      })
      .catch((err) => console.error('Error updating version name:', err));
  };

  // Handler for opening the settings dialog
  const handleOpenSettingsDialog = (aiId: string, collection: Collection) => {
    const ai = ais.find(ai => ai.id === aiId);
    setCurrentEditingCollection({ aiId, collectionId: collection._id });
    setEditingInstructions(collection.instruction || '');
    setEditingTemperature(collection.temperature || 0.9);
    setEditingQaPairsLimit(collection.qaPairsLimit || 5);
    setEditingNotificationEmail(ai?.notificationEmail || '');
    setSettingsTabValue(0);
    setSettingsDialogOpen(true);
  };

  // Handler for closing the settings dialog
  const handleCloseSettingsDialog = () => {
    setSettingsDialogOpen(false);
    setCurrentEditingCollection(null);
    setEditingInstructions('');
    setEditingTemperature(0.9);
    setEditingQaPairsLimit(5);
    setEditingNotificationEmail('');
    setSettingsTabValue(0);
  };

  // Handler for saving the settings
  const handleSaveSettings = () => {
    if (!currentEditingCollection) return;

    const { aiId, collectionId } = currentEditingCollection;

    // Update both collection settings and AI notification email
    Promise.all([
      updateCollectionSettings(aiId, collectionId, {
        instruction: editingInstructions,
        temperature: editingTemperature,
        qaPairsLimit: editingQaPairsLimit
      }),
      updateAi(aiId, { notificationEmail: editingNotificationEmail })
    ])
      .then(() => {
        // Update the local state with the updated settings
        setAis(ais.map(ai => {
          if (ai.id === aiId) {
            return {
              ...ai,
              notificationEmail: editingNotificationEmail,
              collections: ai.collections.map(collection =>
                collection._id === collectionId
                  ? { ...collection, instruction: editingInstructions, temperature: editingTemperature, qaPairsLimit: editingQaPairsLimit }
                  : collection
              )
            };
          }
          return ai;
        }));
        handleCloseSettingsDialog();
      })
      .catch((err) => console.error('Error updating settings:', err));
  };

  return (
    <Box>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        My AIs
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1, 
        mb: 2 
      }}>
        <TextField
          label="New AI Title"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          size="small"
          fullWidth={isMobile}
          sx={{ flex: { sm: 1 } }}
        />
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          minWidth: { sm: 'auto' }
        }}>
          <Button 
            variant="contained" 
            onClick={handleCreate}
            fullWidth={isMobile}
          >
            Create
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/import-ai')}
            startIcon={<DownloadIcon />}
            fullWidth={isMobile}
          >
            Import AI
          </Button>
        </Box>
      </Box>

      {ais.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          No AIs created yet. Create your first AI above.
        </Typography>
      )}

      {ais.map((ai) => (
        <Box 
          key={ai.id}
          sx={{
            mb: 3,
            border: '1px solid #ccc',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box 
            sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              bgcolor: '#f5f5f5',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 0 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {editingAiId === ai.id ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    size="small"
                    autoFocus
                  />
                  <Button 
                    color="primary" 
                    size="small"
                    onClick={() => handleSaveEdit(ai.id)}
                  >
                    Save
                  </Button>
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography variant="h6">{ai.title}</Typography>
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={() => handleStartEdit(ai)}
                    sx={{ ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              {ai.description && !editingAiId && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {ai.description}
                </Typography>
              )}
            </Box>
            <Button 
              color="error" 
              variant="outlined" 
              size="small"
              onClick={() => handleDeleteAi(ai.id)}
              fullWidth={isMobile}
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              Delete AI
            </Button>
          </Box>
          
          <Divider />
          
          {ai.collections && ai.collections.length > 0 ? (
            <Box sx={{ p: 1 }}>
              {ai.collections.map((collection) => (
                <Box
                  key={collection._id}
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    m: { xs: 0.5, sm: 1 },
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    bgcolor: 'white',
                    gap: { xs: 1, sm: 0 }
                  }}
                >
                  <Box>
                    {editingVersionId === collection._id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          value={editVersionName}
                          onChange={(e) => setEditVersionName(e.target.value)}
                          size="small"
                          autoFocus
                        />
                        <Button 
                          color="primary" 
                          size="small"
                          onClick={() => handleSaveVersionName(ai.id, collection._id)}
                        >
                          Save
                        </Button>
                        <Button 
                          color="inherit" 
                          size="small"
                          onClick={handleCancelEditVersion}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="subtitle1">
                        Version: {collection.version}
                        {collection.isPublic ? (
                          <Chip 
                            label="Public" 
                            size="small" 
                            color="success" 
                            sx={{ ml: 1 }}
                          />
                        ) : (
                          <Chip 
                            label="Private" 
                            size="small" 
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleStartEditVersion(collection)}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: { sm: 'wrap' }
                  }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth={isMobile}
                      onClick={() => handleOpenSettingsDialog(ai.id, collection)}
                      startIcon={<SettingsIcon />}
                    >
                      Settings
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      fullWidth={isMobile}
                      onClick={() => navigate(`/qna-data/${ai.id}?collectionId=${collection._id}`)}
                    >
                      Edit Q&A
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      fullWidth={isMobile}
                      onClick={() => navigate(`/test-chatbot/${ai.id}?collectionId=${collection._id}`)}
                    >
                      Test
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      fullWidth={isMobile}
                      onClick={() => navigate(`/chat/${ai.id}?collectionId=${collection._id}`)}
                    >
                      Chat
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      fullWidth={isMobile}
                      onClick={() => navigate(`/review/${ai.id}/versions/${collection._id}`)}
                    >
                      Review
                    </Button>
                    <Button 
                      color="primary" 
                      variant="outlined"
                      size="small"
                      onClick={() => handleCloneVersion(ai.id, collection)}
                    >
                      Clone
                    </Button>
                    <Button 
                      color="secondary" 
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/share-ai/${ai.id}?collectionId=${collection._id}`)}
                      startIcon={<ShareIcon />}
                    >
                      Share
                    </Button>
                    <Button 
                      color="error" 
                      variant="outlined"
                      size="small"
                      onClick={() => handleDeleteCollection(ai.id, collection._id)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No versions found for this AI.
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={handleCloseSettingsDialog}
        aria-labelledby="settings-dialog-title"
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle id="settings-dialog-title">AI Settings</DialogTitle>
        <DialogContent>
          <Tabs value={settingsTabValue} onChange={(_, newValue) => setSettingsTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label="Chatbot Settings" />
            <Tab label="Other Settings" />
          </Tabs>

          {settingsTabValue === 0 && (
            <Box>
              <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, borderLeft: '4px solid #ff9800' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>🤖 AI Instructions Guide:</strong> These instructions define how your AI should behave and respond. Include:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1 }}>
                  • <strong>Role & Identity:</strong> What is the AI's exact role? (e.g., "You are a customer support assistant for a programming school")<br/>
                  • <strong>Response Style:</strong> How should it communicate? (formal, friendly, concise, detailed, etc.)<br/>
                  • <strong>Customer Types:</strong> Who are your typical users? (students, parents, beginners, professionals, etc.)<br/>
                  • <strong>Boundaries:</strong> What topics should/shouldn't it handle?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  💡 <strong>Tip:</strong> Put factual information (schedules, prices, policies) in Q&A pairs instead of instructions. Instructions are for behavior, not facts.
                </Typography>
              </Box>
              <TextField
                autoFocus
                multiline
                rows={10}
                fullWidth
                value={editingInstructions}
                onChange={(e) => setEditingInstructions(e.target.value)}
                placeholder="Enter instructions here..."
                variant="outlined"
                sx={{ mb: 3 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Temperature: {editingTemperature}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Temperature controls randomness in responses: 0 = focused and consistent, 1 = creative and varied
                </Typography>
                <Slider
                  value={editingTemperature}
                  onChange={(_, newValue) => setEditingTemperature(newValue as number)}
                  min={0}
                  max={1}
                  step={0.1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.5, label: '0.5' },
                    { value: 1, label: '1' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Q&A Pairs Limit: {editingQaPairsLimit}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Number of Q&A pairs to consider for each query: fewer pairs = more focused responses, more pairs = broader context
                </Typography>
                <Slider
                  value={editingQaPairsLimit}
                  onChange={(_, newValue) => setEditingQaPairsLimit(newValue as number)}
                  min={1}
                  max={25}
                  step={1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 15, label: '15' },
                    { value: 20, label: '20' },
                    { value: 25, label: '25' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Box>
          )}

          {settingsTabValue === 1 && (
            <Box>
              {/* Email Notification Section */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, borderLeft: '4px solid #2196f3' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                  📧 Email Notifications
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Receive an email notification whenever a visitor sends a message to your AI. The email will include the visitor's message and your AI's response.
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  label="Notification Email"
                  value={editingNotificationEmail}
                  onChange={(e) => setEditingNotificationEmail(e.target.value)}
                  placeholder="Enter email address for notifications..."
                  variant="outlined"
                  helperText="Leave empty to disable email notifications"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettingsDialog} color="inherit">Cancel</Button>
          <Button onClick={handleSaveSettings} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyAis;
